import { Table } from '@/components/ui';
import Empty from '@/components/ui/empty';
import MidPrice, {
  midPriceAccessorFn,
} from '@/features/markets/components/columns/mid-price';
import useFundedAccount from '@/hooks/use-funded-account';
import useSymbol from '@/hooks/use-symbol';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { CapitalStatus } from '@/types/enums';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchAccount } from '../../api/get-account';
import Close from './close';
import EntryPrice, { entryPriceAccessorFn } from './entry-price';
import Funding, { fundingAccessorFn } from './funding';
import LiqPrice, { liqPriceAccessorFn, LiqPriceHeader } from './liq-price';
import Symbol, { symbolAccessorFn } from './symbol';
import Tpsl from './tpsl';
import UnrealizedPnl, {
  unrealizedPnlAccessorFn,
  UnrealizedPnlHeader,
} from './unrealized-pnl';
import Value, { valueAccessorFn } from './value';

interface PositionsTableProps {
  bgColor?: string;
  className?: string;
  noDataComponent?: React.ReactNode;
}

export default function PositionsTable({
  bgColor,
  className,
  noDataComponent,
}: PositionsTableProps) {
  const { t } = useTranslation();
  const symbol = useSymbol();

  const capitalStatus = useAccountStore((state) =>
    state.getCapitalStatus(state.address),
  );

  const { isLoading } = useFetchAccount();
  const { isEval } = useFundedAccount();

  const positions = useCurrentAccount((state) => {
    const positions = Object.keys(state.positions);

    if (symbol) {
      const symbolPosIndex = positions.findIndex((pos) => pos === symbol);

      if (symbolPosIndex > 0) {
        const [symbolPos] = positions.splice(symbolPosIndex, 1);

        positions.unshift(symbolPos);
      }
    }

    return positions;
  });

  const columns = useMemo<ColumnDef<string>[]>(
    () => [
      {
        accessorFn: symbolAccessorFn,
        cell: ({ row }) => <Symbol symbol={row.original} />,
        header: t('positionsTable.market'),
        meta: { width: 'w-[11%] min-w-52' },
      },
      // {
      //   accessorFn: sizeAccessorFn,
      //   cell: ({ row }) => <Size symbol={row.original} />,
      //   header: t('positionsTable.size'),
      //   meta: { width: 'w-[10%] min-w-44' },
      //   sortDescFirst: false,
      // },
      {
        accessorFn: valueAccessorFn,
        cell: ({ row }) => <Value symbol={row.original} />,
        header: t('positionsTable.value'),
        meta: { width: 'w-[11%] min-w-44' },
        sortDescFirst: false,
      },
      {
        accessorFn: entryPriceAccessorFn,
        cell: ({ row }) => <EntryPrice symbol={row.original} />,
        header: t('positionsTable.entryPrice'),
        meta: { width: 'w-[11%] min-w-44' },
      },
      {
        accessorFn: midPriceAccessorFn,
        cell: ({ row }) => <MidPrice symbol={row.original} />,
        header: t('positionsTable.midPrice'),
        meta: { width: 'w-[11%] min-w-44' },
      },
      {
        accessorFn: unrealizedPnlAccessorFn,
        header: UnrealizedPnlHeader,
        accessorKey: 'unrealizedPnl',
        cell: ({ row }) => <UnrealizedPnl symbol={row.original} />,
        meta: { width: 'w-[16%] min-w-80' },
      },
      {
        id: 'liqPrice',
        accessorFn: liqPriceAccessorFn,
        cell: ({ row }) => <LiqPrice symbol={row.original} />,
        header: LiqPriceHeader,
        meta: { width: 'w-[11%] min-w-60' },
      },
      {
        id: 'tp-sl',
        cell: ({ row }) => <Tpsl symbol={row.original} />,
        header: t('positionsTable.tpSl'),
        meta: { width: 'w-[16%] min-w-72' },
      },
      {
        accessorFn: fundingAccessorFn,
        cell: ({ row }) => <Funding symbol={row.original} />,
        header: t('positionsTable.funding'),
        meta: { width: 'w-[11%] min-w-44' },
      },
      {
        id: 'close',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Close symbol={row.original} />
          </div>
        ),
        header: t('positionsTable.action'),
        meta: {
          width: `w-[90px] md:w-[110px] min-w-[90px] md:min-w-[110px] sticky right-0 ${bgColor || 'bg-background'} [&>div>div]:ml-auto`,
        },
      },
    ],
    [t],
  );

  if (isEval && capitalStatus !== CapitalStatus.ACTIVE) {
    let desc = '';

    switch (capitalStatus) {
      case CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS:
        desc = "You've failed evaluation by breaching max daily loss.";
        break;
      case CapitalStatus.GOAL_FAILED_MAX_DRAWDOWN:
        desc = "You've failed evaluation by breaching max drawdown.";
        break;
      default:
        desc = `An unknown error occured. Error code: ${capitalStatus?.toUpperCase()}`;
        break;
    }

    return (
      <div className="my-16 flex flex-col items-center justify-center gap-2">
        <div className="text-center text-xl font-medium capitalize sm:text-2xl">
          {t('portfolio.accountNotActive')}
        </div>

        <div className="text-center text-lg text-vestgrey-100 sm:text-xl">
          {desc}
        </div>
      </div>
    );
  }

  return (
    <Table
      id="positions"
      data={positions}
      columns={columns}
      loading={isLoading}
      isInfiniteScroll
      infiniteScrollOverscan={10}
      infiniteScrollEstimateRowSize={57}
      bgColor={bgColor}
      className={className}
      rightGradientClassName="right-[90px] md:right-[110px]"
      emptyComponent={
        noDataComponent || (
          <Empty
            title={t('positionsTable.noPositionsFound')}
            description={t('positionsTable.noPositionsDescription')}
          />
        )
      }
    />
  );
}
